/**
 * `<FigureV2Web>` et `<TracesWeb>` — une figure v2 et le tracé d'un élève,
 * DESSINÉS dans la console (tranche T2 : le travail se retrouve tel qu'il a
 * été fait).
 *
 * Quatre endroits les montrent : la figure d'une fiche relue (`IllustrationWeb`),
 * la figure de l'énoncé d'un essai et le tracé de l'élève dans « Travaux et
 * corrections » (`TravauxEleve`), et la relecture d'un tracé attendu — la
 * solution pas à pas, les tracés fautifs, le bac à sable (`TraceARelire`, par
 * `ToileDeTrace`). Le dessin est calculé par le moteur partagé
 * (`lib/figures-v2-web.ts`) ; ce fichier ne fait que le poser.
 *
 * ─── Pourquoi `dangerouslySetInnerHTML` est sûr ici ─────────────────────────
 *
 * Avec `TexteMaths`, c'est le seul endroit de la console qui l'emploie, et il
 * n'injecte que la sortie de `listeVersSvg` (ici et dans `ToileDeTrace`) : des éléments SVG construits
 * depuis une liste d'affichage fermée, dont tout texte et tout attribut sont
 * échappés (`echapperXml`). Une étiquette qui porterait `<script>` s'afficherait
 * telle quelle, en lettres — et l'import refuse de toute façon tout balisage.
 *
 * ─── Accessibilité ──────────────────────────────────────────────────────────
 *
 * Le `<svg>` porte `role="img"` et la description en `aria-label` ; la même
 * description peut être rendue VISIBLE sous la figure (`decritVisible`) —
 * c'est ce que la relecture d'une fiche demande.
 */
import type { DocumentConstructionDto, TraceDeTravailDto } from '@silex/shared';
import { useMemo, type MouseEvent, type ReactNode } from 'react';

import { svgDeFigure, svgDesTraces } from '../lib/figures-v2-web';

export function FigureV2Web({
  figure,
  decrit,
  decritVisible = false,
  testid = 'figure-v2',
}: {
  /** La figure v2 brute : elle est normalisée ici, jamais crue sur parole. */
  readonly figure: unknown;
  /** Ce que la figure montre, pour qui ne la voit pas. */
  readonly decrit: string;
  readonly decritVisible?: boolean;
  readonly testid?: string;
}): ReactNode {
  const dessin = useMemo(() => svgDeFigure(figure, decrit || 'Figure'), [figure, decrit]);
  return (
    <figure className="fiche-dessin figure-v2" data-testid={testid}>
      {'svg' in dessin ? (
        <div className="figure-v2__toile" dangerouslySetInnerHTML={{ __html: dessin.svg }} />
      ) : (
        /* Une figure que le moteur refuse n'est jamais dessinée à moitié : on le
           DIT, avec sa raison — le relecteur ne prend pas un trou pour un dessin. */
        <p className="fiche-dessin__griefs" role="alert">
          Figure non dessinée : {dessin.griefs.join(' ; ')}
        </p>
      )}
      {decritVisible || !('svg' in dessin) ? (
        <figcaption className="fiche-dessin__decrit">
          <span className="fiche-dessin__decrit-titre">
            Description lue à l’élève qui ne voit pas
          </span>
          {decrit}
        </figcaption>
      ) : null}
    </figure>
  );
}

/**
 * Le tracé de l'élève (plein, à l'accent) et le tracé attendu (pointillé),
 * sur le repère de l'exercice — et sur la figure de son énoncé quand l'essai
 * la garde. Sous la figure : ce que le vérificateur a mesuré, une phrase par
 * contrainte, sans jugement.
 */
export function TracesWeb({
  trace,
  figureDEnonce,
  prenom,
}: {
  readonly trace: TraceDeTravailDto;
  /** La figure v2 de l'énoncé, brute ; absente : le repère du tracé suffit. */
  readonly figureDEnonce?: unknown;
  readonly prenom: string;
}): ReactNode {
  const dessin = useMemo(() => svgDesTraces(trace, figureDEnonce), [trace, figureDEnonce]);
  return (
    <figure className="fiche-dessin figure-v2" data-testid="manuel-travail-trace">
      <div className="figure-v2__toile" dangerouslySetInnerHTML={{ __html: dessin.svg }} />
      <figcaption className="figure-v2__legende">
        <span className="figure-v2__repere figure-v2__repere--eleve">Le tracé de {prenom}</span>
        {trace.attendu === null ? null : (
          <span className="figure-v2__repere figure-v2__repere--attendu">Le tracé attendu</span>
        )}
      </figcaption>
      {trace.constats.length === 0 ? null : (
        <ul className="figure-v2__constats" data-testid="manuel-travail-constats">
          {trace.constats.map((k, i) => (
            <li key={i} className={k.etat === 'tenue' ? undefined : 'li-sub--attention'}>
              {k.texte}
            </li>
          ))}
        </ul>
      )}
    </figure>
  );
}

/**
 * LE BROUILLON DE FIGURE d'un élève (atelier de figures, L4, F5) : le document
 * de construction qu'il a joint à sa démarche, rendu par le MÊME moteur que ses
 * tracés (`superposerLesTraces`, sans tracé attendu). Lecture seule ; jamais
 * noté — la légende le dit.
 */
export function BrouillonWeb({
  document,
  figureDEnonce,
  prenom,
}: {
  readonly document: DocumentConstructionDto;
  /** La figure v2 de l'énoncé, brute ; absente : le repère du brouillon suffit. */
  readonly figureDEnonce?: unknown;
  readonly prenom: string;
}): ReactNode {
  const dessin = useMemo(
    () => svgDesTraces({ eleve: document, attendu: null }, figureDEnonce),
    [document, figureDEnonce],
  );
  return (
    <figure className="fiche-dessin figure-v2" data-testid="manuel-travail-brouillon-figure">
      <div className="figure-v2__toile" dangerouslySetInnerHTML={{ __html: dessin.svg }} />
      <figcaption className="figure-v2__legende">
        <span className="figure-v2__repere figure-v2__repere--eleve">
          Le brouillon de {prenom} — {document.objets.length}{' '}
          {document.objets.length > 1 ? 'objets' : 'objet'}, jamais noté
        </span>
      </figcaption>
    </figure>
  );
}

/**
 * Une toile de tracé : un `<svg>` déjà calculé par `listeVersSvg` (relecture
 * d'un tracé attendu, `lib/relecture-trace.ts`). Avec `onToucher`, chaque
 * clic rend son point dans les unités du `viewBox` — celles du dessin — et la
 * largeur affichée d'une unité, pour régler l'aimant en pixels. C'est le bac
 * à sable : le relecteur trace comme un élève.
 */
export function ToileDeTrace({
  svg,
  onToucher,
  testid,
}: {
  readonly svg: string;
  readonly onToucher?: (
    p: { readonly x: number; readonly y: number },
    pixelsParUnite: number,
  ) => void;
  readonly testid?: string;
}): ReactNode {
  const toucher = (e: MouseEvent<HTMLDivElement>): void => {
    const el = e.currentTarget.querySelector('svg');
    const ctm = el?.getScreenCTM();
    if (!el || !ctm || !onToucher) return;
    const pt = el.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const p = pt.matrixTransform(ctm.inverse());
    onToucher({ x: p.x, y: p.y }, ctm.a);
  };
  return (
    <div
      className={onToucher ? 'figure-v2__toile figure-v2__toile--active' : 'figure-v2__toile'}
      data-testid={testid}
      onClick={onToucher ? toucher : undefined}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
